import {
    COLUMN_GAP,
    ELEMENT_SELECTORS,
    ITEM_LOCATION_TYPES,
    ROADMAP_TYPES,
    SWIMLANE_GUTTER_WIDTH,
    TIME_PERIOD,
    TRANSITION_DURATION_MS,
} from '@/roadmaps/constants/roadmaps.constants';
import { calculateFirstPeriodScrollAndWidth } from '@/roadmaps/helpers/roadmaps-date.helper';
import { useBlades } from '@/roadmaps/use/use-blades';
import { getColumnWidth } from '@/roadmaps/use/use-columns';
import { duplicateRoadmap } from '@/vuex/modules/roadmaps/api/roadmaps.api';
/* eslint-disable no-restricted-imports */

import store from '@/vuex/store';
import { useVocItemsStore } from '@/stores/voc-items.store';
import html2canvas from 'html2canvas';
import filter from 'lodash/filter';
import { asyncQueue } from '@/vuex/modules/roadmaps/async-queue/async-queue';

let clonedElement;
const ROADMAP_TYPE_NAMES = {
    [ROADMAP_TYPES.TIMELINE]: 'Timeline',
    [ROADMAP_TYPES.COLUMN]: 'Now, Next, Later',
};

export function waitForTransition () {
    const nominalExtraDelay = 150;

    return new Promise((resolve) => setTimeout(resolve, TRANSITION_DURATION_MS + nominalExtraDelay));
}

export function trackRoadmapItemEvent (eventName) {
    const currentRoadmap = store.getters['roadmaps/roadmaps/currentRoadmap'];
    const roadmapItems = filter(store.getters['roadmaps/items/items'], (item) => !item.onBacklog);
    const initiativeItems = filter(roadmapItems, (item) => item.type === ITEM_LOCATION_TYPES.INITIATIVE.key);
    const featureItems = filter(roadmapItems, (item) => item.type === ITEM_LOCATION_TYPES.FEATURE.key);
    const childFeatureItems = filter(featureItems, (item) => item.parentType === ITEM_LOCATION_TYPES.INITIATIVE.key);

    const trackEventData = {
        roadmapId: currentRoadmap.id,
        roadmapType: ROADMAP_TYPE_NAMES[currentRoadmap.type],
        roadmapItemCount: roadmapItems.length,
        roadmapInitiativeCount: initiativeItems.length,
        roadmapFeatureCount: featureItems.length,
        roadmapChildFeatureCount: childFeatureItems.length,
    };

    window.pendo.track(eventName, trackEventData);
}

export function trackRoadmapShareEvent (eventName) {
    const currentRoadmap = store.getters['roadmaps/roadmaps/currentRoadmap'];
    const roadmapItems = filter(store.getters['roadmaps/items/items'], (item) => !item.onBacklog);
    const sharedWithUserViewer = filter(currentRoadmap.userSharing, (user) => user.permission === 'view');
    const sharedWithUserEditor = filter(currentRoadmap.userSharing, (user) => user.permission === 'edit');

    const trackEventData = {
        roadmapId: currentRoadmap.id,
        roadmapType: ROADMAP_TYPE_NAMES[currentRoadmap.type],
        roadmapItemCount: roadmapItems.length,
        sharedWithSubscription: currentRoadmap.subscriptionSharing.permission,
        sharedWithUserEditorCount: sharedWithUserEditor.length,
        sharedWithUserViewerCount: sharedWithUserViewer.length,
    };

    window.pendo.track(eventName, trackEventData);
}

export function trackRoadmapOpenedEvent (eventName) {
    const currentRoadmap = store.getters['roadmaps/roadmaps/currentRoadmap'];

    const trackEventData = {
        roadmapId: currentRoadmap.id,
        userPermission: currentRoadmap.permission,
    };

    window.pendo.track(eventName, trackEventData);
}

export async function downloadRoadmap (zoomLevel) {
    const { closeAllBlades } = useBlades();
    await closeAllBlades();

    const currentRoadmap = store.getters['roadmaps/roadmaps/currentRoadmap'];

    if (currentRoadmap.type === ROADMAP_TYPES.TIMELINE) {
        clonedElement = document.querySelector('.timeline-body').cloneNode(true);
        clonedElement.classList.add('print-version');

        document.body.appendChild(clonedElement);

        const { firstPeriodScroll, periodWidth } = calculateFirstPeriodScrollAndWidth({
            timePeriod: TIME_PERIOD.QUARTER,
            roadmapStartDate: currentRoadmap.startDate,
            items: store.getters['roadmaps/items/items'],
            zoomLevel,
        });

        const clonedSwimlaneBodiesElement = document.querySelectorAll(
            `.print-version .${ELEMENT_SELECTORS.DOWNLOAD_SCROLL}`,
        );
        clonedSwimlaneBodiesElement.forEach((el) => {
            el.scrollLeft = firstPeriodScroll;
        });

        const windowWidth = periodWidth + 150; // add extra for padding either side
        /**
         * added ignoreElements for the vue-wrapper as it caused the
         * vue lifecycle hooks to run and remounted some components
         * this caused some issues with components such as drawers
         */
        const canvas = await html2canvas(clonedElement, {
            windowWidth,
            ignoreElements: (el) => {
                return el.nodeName === 'VUE-WRAPPER';
            },
        });

        const filename = `${currentRoadmap.title.toLowerCase().replace(/([^a-z0-9]+)/gi, '-')}.png`;
        const url = canvas.toDataURL('image/png');
        const downloadLink = document.createElement('a');
        downloadLink.href = url;
        downloadLink.download = filename;
        downloadLink.click();
        downloadLink.remove();
        clonedElement.parentNode.removeChild(clonedElement);
    } else if (currentRoadmap.type === ROADMAP_TYPES.COLUMN) {
        clonedElement = document.querySelector('.column-body').cloneNode(true);
        clonedElement.classList.add('print-version');

        document.body.appendChild(clonedElement);

        const padding = SWIMLANE_GUTTER_WIDTH + COLUMN_GAP;

        const windowWidth = getColumnRoadmapWidth() + padding;
        const windowHeight = clonedElement.clientHeight;

        const canvas = await html2canvas(clonedElement, {
            windowWidth,
            windowHeight,
            ignoreElements: (el) => {
                return el.nodeName === 'VUE-WRAPPER';
            },
        });

        const filename = `${currentRoadmap.title.toLowerCase().replace(/([^a-z0-9]+)/gi, '-')}.png`;
        const url = canvas.toDataURL('image/png');
        const downloadLink = document.createElement('a');
        downloadLink.href = url;
        downloadLink.download = filename;
        downloadLink.click();
        downloadLink.remove();
        clonedElement.parentNode.removeChild(clonedElement);
    }
}

export function getColumnRoadmapWidth () {
    const columns = store.getters['roadmaps/columns/columns'];
    const columnCount = Object.keys(columns).length;

    const gutterCount = columnCount - 1;

    const padding = SWIMLANE_GUTTER_WIDTH * 2 + COLUMN_GAP * gutterCount;

    const columnWidths = getColumnWidth() * columnCount;

    return columnWidths + padding;
}

export async function cloneRoadmap (roadmapId) {
    let newRoadmapId;
    const task = async () => {
        const duplicatedRoadmap = await duplicateRoadmap(roadmapId);
        newRoadmapId = duplicatedRoadmap.id;
    };

    await asyncQueue.enqueue({ task, updateType: 'noUpdate' });

    return newRoadmapId;
}

export function canAddIdeasToRoadmap () {
    const hasVocProductLine = store.getters['productLines/hasProductLinePurchased']('voc');
    const { canEditVocItems } = useVocItemsStore();

    return hasVocProductLine ? canEditVocItems : true;
}
